import handleTeamAssistV2Error from 'actions/team_assist_v2/lib/handle_team_assist_v2_error';
import { finishRequest, startRequest } from 'actions/team_assist_v2/lib/in_flight_team_assist_v2_requests';
import isTeamAssistV2Enabled from 'actions/team_assist_v2/lib/is_team_assist_v2_enabled';
import mapTeamAssistV2Session from 'actions/team_assist_v2/lib/map_team_assist_v2_session';
import Err from 'models/err';
import TeamAssistV2Turn from 'models/team_assist_v2_turn';

export default class RequestTeamAssistV2 {
  constructor(context) {
    this.context = context;
  }

  run({ customerId, isRefreshing = false }) {
    if (!isTeamAssistV2Enabled(this.context.stores.appFeatures.get())) {
      return;
    }
    if (!customerId || !this.context.stores.customers.has({ id: customerId })) {
      return;
    }
    const store = this.context.stores.customers.storesFor(customerId).teamAssistsV2;
    // Until a Clear's DELETE answers, glados would hand back the session being
    // cleared. A Clear that fails loads the session again itself.
    if (store.isClearing()) {
      return;
    }
    const controller = startLoad(customerId);
    store.clearErrorForLoading();
    if (!isRefreshing) {
      store.setLoading();
    }

    return this.context.gateways.teamAssistV2
      .loadActive({ customerId, signal: controller.signal })
      .then(view => {
        if (controller.signal.aborted) {
          return;
        }
        const current = this.context.stores.customers.storesFor(customerId).teamAssistsV2;
        const loaded = mapTeamAssistV2Session((view && view.messages) || [], (view && view.ratings) || []).map(turn =>
          TeamAssistV2Turn.create(turn)
        );
        current.set(mergeLoadedTurns(current.findAll(), loaded));
        current.markLoaded();
      })
      .catch(err => {
        if (controller.signal.aborted) {
          return;
        }
        handleTeamAssistV2Error(this.context, err);
        // The panel shows the failure with Retry: an empty panel would read as
        // a new session, while glados may still hold one.
        const current = this.storeFor(customerId);
        if (current) {
          current.setErrorForLoading(new Err({ code: Err.Code.OPERATION_FAILED, detail: err && err.message }));
        }
      })
      .finally(() => {
        if (!finishLoad(customerId, controller)) {
          return;
        }
        const current = this.storeFor(customerId);
        if (current) {
          current.resetLoading();
        }
      });
  }

  storeFor(customerId) {
    if (!this.context.stores.customers.has({ id: customerId })) {
      return null;
    }
    return this.context.stores.customers.storesFor(customerId).teamAssistsV2;
  }
}

const latestLoadByCustomerId = new Map();

// Stops the customer's session load, if one is out, so it writes nothing; its
// finish still ends the loading. Streaming turns are left alone.
export function abortTeamAssistV2Load(customerId) {
  const latest = latestLoadByCustomerId.get(customerId);
  if (latest) {
    latest.abort();
  }
}

// A reconnect can start a load while another is out, and only the latest may
// write to the store, so starting one stops the one before.
function startLoad(customerId) {
  const previous = latestLoadByCustomerId.get(customerId);
  if (previous) {
    previous.abort();
  }
  const controller = startRequest(customerId);
  latestLoadByCustomerId.set(customerId, controller);
  return controller;
}

// Whether `controller` was the latest load, whose finish ends the loading.
function finishLoad(customerId, controller) {
  finishRequest(customerId, controller);
  if (latestLoadByCustomerId.get(customerId) !== controller) {
    return false;
  }
  latestLoadByCustomerId.delete(customerId);
  return true;
}

// Keeps the turns streamed here that the session doesn't hold yet. glados gives
// a question its own id, which such a turn learns from its answer's `questionId`.
function mergeLoadedTurns(current, loaded) {
  const loadedIds = new Set(loaded.map(turn => turn.id));
  const unsaved = current.filter(turn => turn.live || (wasStreamedHere(turn) && !loadedIds.has(questionIdOf(turn))));
  return [...loaded, ...unsaved];
}

function wasStreamedHere(turn) {
  return turn.id !== questionIdOf(turn);
}

function questionIdOf(turn) {
  return turn.answer ? turn.answer.questionId : null;
}
